import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const NEAR_BOTTOM_ROOT_MARGIN = '0px 0px 64px 0px';

type UseChatAutoScrollCountdownOptions = {
  conversationId: string;
  localActivityKey: string;
  remoteActivityKey: string;
  scrollContainer: HTMLElement | null;
};

export function useChatAutoScrollCountdown({
  conversationId,
  localActivityKey,
  remoteActivityKey,
  scrollContainer,
}: UseChatAutoScrollCountdownOptions) {
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const previousConversationIdRef = useRef<string | undefined>(undefined);
  const previousLocalActivityKeyRef = useRef(localActivityKey);
  const previousRemoteActivityKeyRef = useRef(remoteActivityKey);
  const isPinnedToBottomRef = useRef(true);
  const [isCountdownVisible, setIsCountdownVisible] = useState(false);
  const [countdownInstanceKey, setCountdownInstanceKey] = useState(0);
  const scrollContainerRef = useRef(scrollContainer);
  scrollContainerRef.current = scrollContainer;

  const scrollContainerToEnd = useCallback((behavior: ScrollBehavior) => {
    const container = scrollContainerRef.current;
    container?.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  const scrollToBottom = useCallback(() => {
    scrollContainerToEnd('smooth');
    setIsCountdownVisible(false);
    isPinnedToBottomRef.current = true;
  }, [scrollContainerToEnd]);

  const dismissCountdown = useCallback(() => {
    setIsCountdownVisible(false);
  }, []);

  useEffect(() => {
    const anchor = bottomAnchorRef.current;
    if (!anchor || !scrollContainer) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        isPinnedToBottomRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          setIsCountdownVisible(false);
        }
      },
      {
        root: scrollContainer,
        rootMargin: NEAR_BOTTOM_ROOT_MARGIN,
        threshold: 0,
      }
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [conversationId, scrollContainer]);

  useEffect(() => {
    if (!scrollContainer || typeof ResizeObserver === 'undefined') return;
    const stickToBottom = () => {
      if (isPinnedToBottomRef.current) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    };
    const observer = new ResizeObserver(stickToBottom);
    observer.observe(scrollContainer);
    Array.from(scrollContainer.children).forEach((child) =>
      observer.observe(child)
    );
    return () => observer.disconnect();
  }, [scrollContainer]);

  useEffect(() => {
    const isConversationChange =
      previousConversationIdRef.current !== conversationId;
    const isLocalActivityChange =
      previousLocalActivityKeyRef.current !== localActivityKey;
    const isRemoteActivityChange =
      previousRemoteActivityKeyRef.current !== remoteActivityKey;

    previousConversationIdRef.current = conversationId;
    previousLocalActivityKeyRef.current = localActivityKey;
    previousRemoteActivityKeyRef.current = remoteActivityKey;

    if (isConversationChange || isLocalActivityChange) {
      isPinnedToBottomRef.current = true;
      setIsCountdownVisible(false);
      scrollContainerToEnd(isConversationChange ? 'auto' : 'smooth');
      return;
    }

    if (!isRemoteActivityChange) {
      return;
    }

    if (isPinnedToBottomRef.current) {
      scrollContainerToEnd('smooth');
      return;
    }

    setIsCountdownVisible(true);
    setCountdownInstanceKey((key) => key + 1);
  }, [
    conversationId,
    localActivityKey,
    remoteActivityKey,
    scrollContainerToEnd,
  ]);

  return useMemo(
    () => ({
      bottomAnchorRef,
      countdownInstanceKey,
      dismissCountdown,
      isCountdownVisible,
      scrollToBottom,
    }),
    [countdownInstanceKey, dismissCountdown, isCountdownVisible, scrollToBottom]
  );
}
